import { useState, useEffect, useCallback } from "react";
import { Loader2, Search, BookUser, ShieldCheck, Smartphone } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";

// Sélecteur de destinataire depuis l'annuaire sécurisé du workspace actif.
// channel="phone" : affiche le téléphone ; un contact sans numéro n'est pas sélectionnable.
export const DirectoryPicker = ({ open, onOpenChange, onSelect, channel = "email" }) => {
  const isPhone = channel === "phone";
  const { t } = useTranslation("directory");
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/directory/contacts", { params: { search: search || undefined, filter: "all" } });
      setContacts(data.filter((c) => c.status === "active"));
    } catch (e) { setContacts([]); } finally { setLoading(false); }
  }, [search]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(load, 220);
    return () => clearTimeout(t);
  }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="directory-picker-dialog" className="max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BookUser className="w-5 h-5 text-emerald-600" />{t("picker.title")}</DialogTitle>
          <DialogDescription>{isPhone ? t("picker.descPhone") : t("picker.descEmail")}</DialogDescription>
        </DialogHeader>
        <div className="relative mb-3">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="directory-picker-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("picker.search")} className="pl-9 bg-slate-50 border-slate-200" />
        </div>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : contacts.length === 0 ? (
          <div className="text-sm text-slate-400 py-6 text-center">{t("picker.empty")}</div>
        ) : (
          <div className="space-y-1.5">
            {contacts.map((c) => {
              const unavailable = isPhone && !c.phone;
              return (
              <button
                key={c.id}
                type="button"
                data-testid={`directory-picker-contact-${c.id}`}
                disabled={unavailable}
                aria-disabled={unavailable}
                onClick={() => { onSelect(c); onOpenChange(false); }}
                className={`w-full text-left rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 transition-colors ${unavailable ? "opacity-50 cursor-not-allowed" : "hover:border-emerald-300 hover:bg-emerald-50/40"}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{c.display_name || c.email}{c.organization_name ? ` — ${c.organization_name}` : ""}</div>
                    <div className="text-xs text-slate-500 truncate">{isPhone ? c.phone : c.email}</div>
                    {unavailable && <div data-testid={`directory-picker-no-phone-${c.id}`} className="text-[11px] text-slate-500 mt-0.5">{t("picker.noPhone")}</div>}
                  </div>
                </div>
                {/* Canaux fiables : uniquement si réellement validés (§40/§41) */}
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {c.email_verified && <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600"><ShieldCheck className="w-3 h-3" />{t("picker.emailVerified")}</span>}
                  {c.phone_verified && <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600"><ShieldCheck className="w-3 h-3" />{t("picker.phoneVerified")}</span>}
                  {c.secure_device_status === "active" && <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600"><Smartphone className="w-3 h-3" />{t("picker.secureDevice")}</span>}
                  {!c.email_verified && !c.phone_verified && c.secure_device_status !== "active" && <span className="text-[11px] text-slate-400">{t("picker.noVerifiedChannel")}</span>}
                </div>
              </button>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DirectoryPicker;
